/**
 * Search tracking initialization and coordination module.
 * This module serves as the main entry point for search tracking functionality,
 * coordinating between different tracking handlers and managing the tracking lifecycle.
 *
 * @module tracking-events
 * @see tracking-config For configuration types
 * @see tracking-handlers For event handling implementations
 */

import createTrackingConfig, { type TrackingConfiguration } from './utils/tracking-config';
import type { SearchTrackingConfig } from './types/tracking-config';
import { logDebug } from './utils/tracking-logger';
import { findSearchContainer, findSearchToggle } from './utils/dom-utils';
import {
  setupSearchToggleListeners,
  getSearchElements,
  setupSearchInputListeners,
  setupShowAllResultsListeners,
  setupContainerObserver,
  handleSearchToggleClick,
  handleSearchToggleKeyDown,
  cleanupSearchEventListeners
} from './utils/tracking-handlers';

/** Configuration state for search tracking */
let tracking: TrackingConfiguration;

/**
 * Initializes search tracking functionality with optional configuration.
 * Sets up all necessary event listeners and tracking handlers.
 * 
 * @param {SearchTrackingConfig} [config] - Optional configuration object to customize tracking behavior
 * @returns {void}
 * @throws {Error} If tracking configuration is invalid
 * 
 * @example
 * initSearchTracking({
 *   typingInterval: 300,
 *   initDelay: 400,
 *   options: {
 *     enableBloomreach: true,
 *     enableSalesfire: true
 *   }
 * });
 */
const initSearchTracking = (config?: SearchTrackingConfig): void => {
  tracking = createTrackingConfig(config);
  logDebug("Initializing search toggle listeners with config:", tracking);

  attachSearchToggleListeners();
};

/**
 * Sets up event listeners for search toggle buttons.
 * Creates and attaches click and keydown handlers with proper initialization delays.
 * Uses wrapped handlers to prevent multiple initializations.
 *
 * @private
 * @returns {void}
 * @throws {Error} If search toggle element cannot be found
 */
const attachSearchToggleListeners = (): void => {
  const searchToggle = findSearchToggle();
  if (!searchToggle) {
    logDebug("Search toggle not found");
    return;
  }

  const clickHandler = (e: Event): void => {
    handleSearchToggleClick(e, initializeSearchInputs, tracking.searchConfig.initDelay);
  }

  const keyHandler = (e: KeyboardEvent): void => {
    handleSearchToggleKeyDown(e, initializeSearchInputs, tracking.searchConfig.initDelay);
  }

  setupSearchToggleListeners(searchToggle, clickHandler, keyHandler);
};

/**
 * Initializes search input tracking.
 * Sets up input listeners, results page tracking, and container observers.
 * This function is called after the search panel is opened.
 *
 * @private
 * @returns {void}
 * @throws {Error} If search inputs cannot be initialized
 *
 * @example
 * // Called internally after search panel opens
 * initializeSearchInputs();
 */
const initializeSearchInputs = (): void => {
  logDebug("Initializing search tracking");

  const { searchInputs, containerElements } = getSearchElements(tracking);
  if (searchInputs.length === 0) return;

  setupInputTracking(searchInputs, containerElements);
};

/**
 * Sets up the input tracking for the search inputs.
 *
 * @param searchInputs The search input elements.
 * @param containerElements The container elements.
 */
const setupInputTracking = (searchInputs: HTMLInputElement[], containerElements: HTMLElement[]): void => {
  const searchContainer = findSearchContainer();

  setupSearchInputListeners(searchInputs, tracking);
  setupShowAllResultsListeners(tracking);

  setupContainerObserver(containerElements, tracking, () => {
    // Clean up all listeners
    for (const input of searchInputs) {
      cleanupSearchEventListeners(input, searchContainer);
    }
    attachSearchToggleListeners();
  });
}

export default initSearchTracking;
